import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort, SortDirection } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import {
    DashboardsServiceV2,
    DimensionColumnTypeInd,
    DimensionDefinitionDTO,
    WidgetDataDTO,
    WidgetDataSetRequestDTO,
    WidgetDefinitionDTO,
    WidgetFilterDto,
} from 'src/app/core/services/dashboardsv2.service';
import { DashboardWidgetLoadingService } from '../../../dashboard-widget-loading-service';

@Component({
    selector: 'app-widget-table',
    templateUrl: './widget-table.component.html',
    styleUrls: ['./widget-table.component.scss'],
    providers: [DashboardsServiceV2],
})
export class TableWidgetComponent implements OnInit, OnChanges, OnDestroy {
    @Input() widgetConfig: WidgetDefinitionDTO;
    @Input() dataSetRequest: WidgetDataSetRequestDTO;
    @Input() loadWidgetData: boolean;

    @Output() mapDialogButtonClicked = new EventEmitter<string>();

    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild('filterInput') filterInput: HTMLInputElement;

    private _sort: MatSort;
    @ViewChild(MatSort) set sort(value: MatSort) {
        this._sort = value;
        if (this.dataSource) {
            this.dataSource.sort = this._sort;
        }
    }

    dataSource: MatTableDataSource<any[]>;

    columns: {
        // unique string for each column
        columnDef: string;
        // header text
        header: string;
        // used when header groups are in place for ungrouped headers
        headerRowSpan?: number;
        displayHeader: boolean;
        // class to apply on the column cells
        class: string;
        // displays a percentage-width bar in the table cell
        isBar: boolean;
        // url for map pop-up buttons
        headerUrl: string;
        index: number;
        sticky: boolean;
        cellUrl: (row: any[]) => string;
        // function to get the display value of the column cells
        cell: (row: any[]) => string;
        cellColour: (row: any[]) => string;
    }[];

    headerGroups: {
        columnDef: string;
        name: string;
        colSpan: number;
    }[] = [];
    displayedHeaderGroups: string[] = [];

    displayedColumns: string[] = [];
    subscription: any;
    isLoaded: boolean = false;
    hasFilters: boolean;
    sortActive: string;
    sortDirection: SortDirection;
    tableFilter = '';
    widgetData: WidgetDataDTO; // Ensure widgetData is declared

    public lastLoadedDataSet: WidgetDataSetRequestDTO | undefined = undefined;

    constructor(
        private dashboardService: DashboardsServiceV2,
        private readonly _dashboardWidgetLoadingService: DashboardWidgetLoadingService
    ) {}

    ngOnInit(): void {
        if (this.widgetConfig.widgetFilters && this.widgetConfig.widgetFilters.length > 0) {
            this.hasFilters = true;
        }

        if (this.loadWidgetData) {
            this.loadData();
        }
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        const hasDataSetChangedSinceLastLoaded =
            JSON.stringify(this.dataSetRequest) !== JSON.stringify(this.lastLoadedDataSet);

        if (changes.dataSetRequest && !changes.dataSetRequest.isFirstChange()) {
            if (this.loadWidgetData && this.widgetConfig.version != 3) {
                this.loadData();
            } else {
                this.markAsLoading();
            }
        }

        if (this.loadWidgetData && hasDataSetChangedSinceLastLoaded && this.widgetConfig.version != 3) {
            this.loadData();
        }

        if (changes.loadWidgetData && !this.isLoaded && !changes.loadWidgetData.isFirstChange()) {
            if (changes.loadWidgetData.currentValue) {
                this.loadData();
            }
            //stop loading unfinished widget
            if (!changes.loadWidgetData.currentValue && this.subscription) {
                this.subscription.unsubscribe();
                this.widgetConfig.loading = false;
                this._dashboardWidgetLoadingService.remove(this.widgetConfig.id.toString());
            }
        }

        // Handle updates for version 3 dashboards where data is pushed directly via widgetConfig.data
        if (changes.widgetConfig && this.widgetConfig.version === 3) {
            if (this.widgetConfig.data) {
                this.widgetData = this.widgetConfig.data;
                this.initializeTableData(this.widgetData);
            }
        }
    }

    loadData() {
        this.widgetConfig.loading = true;
        this.markAsLoading();

        let request: WidgetDataSetRequestDTO = JSON.parse(JSON.stringify(this.dataSetRequest));

        if (this.subscription) {
            this.subscription.unsubscribe();
        }

        if (this.widgetConfig.version === 3 && this.widgetData) {
            // If version 3 and data is already provided, just initialize the table
            this.widgetConfig.loading = false;
            this.markAsLoaded();
            this.initializeTableData(this.widgetData);
            return;
        }

        if (this.hasFilters) {
            this.widgetConfig.widgetFilters.forEach((widgetFilter: WidgetFilterDto) => {
                request.filters.push({
                    filterTypeInd: 0,
                    filterId: widgetFilter.filterId,
                    filterValues: [widgetFilter.filterSelection],
                });
            });
        }

        this.subscription = this.dashboardService
            .getDataForWidget(this.widgetConfig, request)
            .subscribe((response: WidgetDataDTO) => {
                this.widgetData = response;
                this.initializeTableData(this.widgetData); // Call initializeTableData here
            });
    }

    initializeTableData(response: WidgetDataDTO): void {
        this.columns = [];
        this.headerGroups = [];
        this.displayedColumns = [];
        this.displayedHeaderGroups = [];

        response.dimensions
            .filter((dimension: DimensionDefinitionDTO) => !dimension.hidden)
            .map((item, index, array) => {
                const columnDef = index + '-' + item.name.replace(new RegExp(/\s/g), '');
                if (!item.headerColSpan) {
                    this.columns.push({
                        columnDef: columnDef,
                        header: item.name,
                        headerRowSpan: null,
                        displayHeader: item.headerRowSpan == null,
                        class:
                            (this.widgetConfig.tableGraph && index == array.length - 1) ||
                            item.columnType == DimensionColumnTypeInd.HorizontalBar
                                ? 'bar'
                                : this.isNumber(response.data[0]?.[index])
                                ? 'number'
                                : 'text',
                        isBar:
                            (this.widgetConfig.tableGraph && index == array.length - 1) ||
                            item.columnType == DimensionColumnTypeInd.HorizontalBar,
                        headerUrl: item.url,
                        index: index,
                        sticky: index == 0 && !this.isNumber(response.data[0]?.[index]),
                        cellUrl: (row: any) => row[index].url ?? null,
                        cell: (row: any) => this.numberWithCommas(row[index]?.displayValue ?? row[index]),
                        cellColour: (row: any) =>
                            item.colours.length > 0
                                ? this.applyHeatMap(row[index], index, item.colours[0], item.colours[1])
                                : 'inherit',
                    });
                    this.displayedColumns.push(columnDef);
                }

                if (item.headerRowSpan) {
                    if (this.columns[this.columns.length - 1]) {
                        this.columns[this.columns.length - 1].class += ' border-right';
                    }
                    this.columns.push({
                        columnDef: columnDef + '-2',
                        header: item.name,
                        headerRowSpan: item.headerRowSpan,
                        displayHeader: true,
                        class:
                            (this.widgetConfig.tableGraph && index == array.length - 1
                                ? 'bar'
                                : this.isNumber(response.data[0]?.[index])
                                ? 'number'
                                : 'text') + ' border-right',
                        isBar:
                            (this.widgetConfig.tableGraph && index == array.length - 1) ||
                            item.columnType == DimensionColumnTypeInd.HorizontalBar,
                        headerUrl: item.url,
                        index: index,
                        sticky: index == 0 && !this.isNumber(response.data[0]?.[index]),
                        cellUrl: (row: any) => row[index]?.url ?? null,
                        cell: (row: any) => this.numberWithCommas(row[index]?.displayValue ?? row[index]),
                        cellColour: (row: any) =>
                            item.colours.length > 0
                                ? this.applyHeatMap(row[index], index, item.colours[0], item.colours[1])
                                : 'inherit',
                    });
                    this.displayedHeaderGroups.push(columnDef + '-2');
                }

                if (item.headerColSpan) {
                    this.headerGroups.push({
                        columnDef: columnDef,
                        name: item.name,
                        colSpan: item.headerColSpan,
                    });
                    if (this.columns[this.columns.length - 1]) {
                        this.columns[this.columns.length - 1].class += ' border-right';
                    }
                    this.displayedHeaderGroups.push(columnDef);
                }
            });

        this.widgetConfig.loading = false;
        this.markAsLoaded();
        this._updateLastLoadedDataSet();

        if (response.data) {
            //remove hidden dimensions from data
            response.dimensions.forEach((dimension: DimensionDefinitionDTO, index: number) => {
                if (dimension.hidden) {
                    response.data.forEach((row: any[]) => {
                        row.splice(index, 1);
                    });
                }
            });

            this.dataSource = new MatTableDataSource<any[]>(response.data);
            this.dataSource.paginator = this.paginator;
            this.dataSource.sortingDataAccessor = (data, sortHeaderId) => this.getSortData(data, sortHeaderId);
            this.dataSource.filterPredicate = (data, filter) => {
                return data.some((value) => {
                    const filterValue: string = this.numberWithCommas(value.displayValue ?? value).toLowerCase();
                    return filterValue.includes(filter);
                });
            };

            // Set default sorting if none is configured
            if (this.widgetConfig.sortFieldIndex == null) {
                // Find the "Customers" column and set it as default sort (descending)
                const customersColumn = this.columns.find((col) => col.header === 'Customers' && col.displayHeader);
                if (customersColumn) {
                    this.widgetConfig.sortFieldIndex = customersColumn.index;
                    this.widgetConfig.sortFieldAscending = false; // false = descending (largest to smallest)
                }
            }

            if (this.widgetConfig.sortFieldIndex != null) {
                this.sortActive = this.columns.find(
                    (col) => col.index == this.widgetConfig.sortFieldIndex && col.displayHeader
                )?.columnDef;
                this.sortDirection = this.widgetConfig.sortFieldAscending ? 'asc' : 'desc';
                if (this._sort) {
                    this._sort.sort({
                        id: this.sortActive,
                        start: this.sortDirection,
                        disableClear: false,
                    });
                }
            }

            if (this.widgetConfig.tableFilter) {
                this.tableFilter = this.widgetConfig.tableFilter;
                this.applyFilter(this.tableFilter);
            } else {
                this.tableFilter = '';
                this.applyFilter('');
            }
        }
    }

    updateWidgetFilter(filterId: string, filterKey: number) {
        const widgetFilter = this.widgetConfig.widgetFilters.find((filter) => filter.filterId === filterId);
        if (widgetFilter.filterSelection != filterKey) {
            widgetFilter.filterSelection = filterKey;
            this.loadData();
        }
    }

    private getSortData(data: any[], sortHeaderId: string): string | number {
        const column = this.columns.find((x) => x.columnDef === sortHeaderId);
        if (column) {
            const value: string = (data[column.index].displayValue ?? data[column.index]).toString();
            //check for and convert value to number
            if (this.isNumber(value)) {
                const numberValue = this.convertStringToNumber(value);
                if (numberValue !== null) {
                    // Ensure null check
                    return numberValue;
                }
            }
            return value; // Return original string value if not a number or conversion fails
        }
        return '';
    }

    private isNumber(value: any): boolean {
        // Changed type to any for robustness
        if (typeof value === 'number') {
            return true;
        }
        if (typeof value === 'string') {
            const numberRegex = new RegExp(/^(?:- ?)?(?:[$£] ?)?(?:\d+|\d{1,3}(?:\,\d{3})+)(?:\.\d+)?(?: ?%)?$/);
            return numberRegex.test(value);
        }
        return false;
    }

    private convertStringToNumber(value: any): number | null {
        // Changed type to any and return type to number | null
        if (typeof value === 'number') {
            return value;
        }
        if (typeof value === 'string') {
            const strippedValue = value.replace(/[^\d\.-]/g, ''); //strip all but numbers, decimal points, and negative symbols
            if (!isNaN(+strippedValue)) {
                return +strippedValue;
            }
        }
        return null;
    }

    handleFilterKeyUp(event: Event): void {
        const filterValue = (event.target as HTMLInputElement).value;
        this.applyFilter(filterValue);
    }

    applyFilter(filter: string): void {
        this.dataSource.filter = filter.trim().toLowerCase();
        this.widgetConfig.tableFilter = this.dataSource.filter;

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    numberWithCommas(item: any) {
        if (typeof item === 'number') {
            return item.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        } else if (typeof item === 'string') {
            return item;
        }
        return item; // Fallback for other types
    }

    openMapDialog(event: MouseEvent, url: string) {
        event.stopPropagation();
        this.mapDialogButtonClicked.emit(url);
    }

    applyHeatMap(value: any, dataIndex: number, lowerColour: string, upperColour: string): string {
        const numberValue = this.convertStringToNumber(value);
        if (numberValue !== null && numberValue !== 0) {
            // Ensure null check
            const dataSet = this.dataSource.data
                .filter((row) => this.isNumber(row[dataIndex]))
                .map((row) => this.convertStringToNumber(row[dataIndex]))
                .filter((val) => val !== null && val !== 0); // Ensure null check here too

            if (dataSet.length > 0) {
                const threshold = dataSet.reduce((a, b) => (numberValue < 0 ? Math.min(a, b) : Math.max(a, b)));
                const percentage = threshold !== 0 ? numberValue / threshold : 0; // Avoid division by zero

                const alphaHex = Math.round(percentage * 255)
                    .toString(16)
                    .padStart(2, '0');

                return (numberValue < 0 ? lowerColour : upperColour) + alphaHex;
            }
        }
        return 'inherit';
    }

    updateSortSelection(event: Sort): void {
        const index = this.columns.find((x) => x.columnDef === event.active)?.index;
        if (index != null) {
            this.widgetConfig.sortFieldIndex = index;
            this.widgetConfig.sortFieldAscending = event.direction === 'asc';
        }
    }

    public markAsLoading(): void {
        this.isLoaded = false;

        const widgetId = this.widgetConfig.id.toString();

        this._dashboardWidgetLoadingService.add(widgetId);
    }

    public markAsLoaded(): void {
        this.isLoaded = true;

        const widgetId = this.widgetConfig.id.toString();

        this._dashboardWidgetLoadingService.remove(widgetId);
    }

    private _updateLastLoadedDataSet(): void {
        this.lastLoadedDataSet = { ...this.dataSetRequest };
    }
}
